import React from "react";
import { FilteredList } from "@/components/ui/lists/FilteredList";
import { EmployeeSettingsCard } from "@/components/admin/EmployeeSettingsCard/EmployeeSettingsCard";
import type { IServiceEmployee } from "@/services/types";
import styles from "./SettingsSection.module.css";

export interface EmployeeSettingsListProps {
  /** List of employees to display */
  employees: IServiceEmployee[];
  /** Whether any settings update is in progress */
  isUpdating: boolean;

  /** Open the employee settings popup/drawer */
  onOpenEmployeeSettings?: (employee: IServiceEmployee) => void;

  /** Test ID prefix */
  testId?: string;
}

/**
 * EmployeeSettingsList - Filtered list of employees with bookable toggles
 * Displays employees with search/filter and allows toggling their bookable status
 */
export const EmployeeSettingsList: React.FC<EmployeeSettingsListProps> = ({
  employees,
  isUpdating,
  onOpenEmployeeSettings,
  testId = "settings-section",
}) => {
  if (employees.length === 0) {
    return <div className={styles.emptyState}>Ingen ansatte funnet</div>;
  }

  return (
    <div className={styles.employeeListWrapper}>
      <FilteredList
        items={employees}
        filterItem={(employee: IServiceEmployee, normalized) =>
          (employee.name ?? "").toLowerCase().includes(normalized) ||
          employee.email.toLowerCase().includes(normalized) ||
          (employee.department ?? "").toLowerCase().includes(normalized)
        }
        renderItem={(employee: IServiceEmployee) => (
          <EmployeeSettingsCard
            key={employee.id}
            employee={employee}
            isBookable={employee.settings?.is_bookable !== false}
            isUpdating={isUpdating}
            onOpenSettings={
              onOpenEmployeeSettings
                ? () => onOpenEmployeeSettings(employee)
                : undefined
            }
            data-testid={`${testId}-employee-card-${employee.id}`}
          />
        )}
        placeholder="Søk etter ansatt..."
        debounce={150}
        maxHeight="clamp(280px, 55vh, 500px)"
        data-testid={`${testId}-filtered-employee-list`}
      />
    </div>
  );
};

EmployeeSettingsList.displayName = "EmployeeSettingsList";
